import { FunctionComponent } from 'react';

import {
  AttentionIcon,
  Box,
  Button,
  Flex,
  Text,
} from '@joinrepublic/dls-ui-kit';

import { IInvestmentStateEnum } from '@r-client/data/graphql-types';
import { useAnalytics } from '@r-client/shared/data/analytics';

import { IBannerQuery } from '../../graphql/types';

export interface IActionRequiredBannerProps {
  className?: string;
  daysLeft?: number;
  // TODO: https://linear.app/republic/issue/RGQL-293/investment-type-needs-state-value
  investment?: Extract<
    IBannerQuery['banner'],
    { __typename: 'ActionRequiredBanner' }
  >['investment'];
}

export const ActionRequiredBanner: FunctionComponent<
  React.PropsWithChildren<IActionRequiredBannerProps>
> = ({ daysLeft, investment }) => {
  const analytics = useAnalytics();
  let daysLeftMessage = '';
  let actionMessage = '';

  if (investment && (daysLeft || daysLeft === 0) && daysLeft <= 7) {
    if (daysLeft <= 0) {
      daysLeftMessage = "Time's Up!!!";
    } else {
      daysLeftMessage = `${daysLeft} days left`;
    }
  } else {
    daysLeftMessage = 'Action required';
  }

  if (!investment) {
    actionMessage = 'Your action is required to complete your verification';
  } else if (investment?.state === IInvestmentStateEnum.Reserved) {
    actionMessage = `Your reservation in ${investment.offering.companyName} requires your attention`;
  } else if ((daysLeft || daysLeft === 0) && daysLeft <= 7) {
    actionMessage =
      'Your action is required to avoid cancellation of your investments';
  } else {
    actionMessage = `Important action item related to your ${
      investment.investmentType?.toLowerCase().replace(/_/g, ' ') ??
      'investment'
    } in ${investment.offering.companyName}`;
  }

  const analyticsTrackBannerClicked = () => {
    analytics.track({
      name: 'website_banner_clicked',
      params: {
        banner_name: 'ActionRequiredBanner',
        button_name: 'action_required',
        extra_data: {
          companyName: investment?.offering.companyName ?? undefined,
          offeringSlug: investment?.offering.slug ?? undefined,
          daysLeft,
        },
      },
    });
  };

  return (
    <Flex
      direction="column"
      alignItems={{
        base: 'center',
        sm: 'flex-start',
        md: 'center',
        lg: 'center',
      }}
      justifyContent="center"
      bg="gray-f2"
      py="6"
      px="4"
      position="relative"
    >
      <Box flexGrow={1}>
        <Flex textAlign="center" justifyContent={{ md: 'center' }}>
          <Text
            v="2.2"
            fontWeight="semibold"
            color="negative.fg-primary"
            mr={{ base: '0', sm: '2' }}
            mb={{ base: '4', sm: '0' }}
          >
            <AttentionIcon size="small" verticalAlign="-0.25em" mr="2" />
            <Text
              v="2.2"
              as="span"
              fontWeight="semibold"
              color="negative.fg-primary"
              display={{ base: 'none', sm: 'inline-block' }}
            >
              {daysLeftMessage}
            </Text>
          </Text>
          <Text v="2.2" mb={{ base: '4', sm: '0' }}>
            {actionMessage}
          </Text>
        </Flex>
      </Box>
      <Button
        as="a"
        href={
          investment ? `/portfolio/${investment?.offering.slug}` : '/settings'
        }
        onClick={analyticsTrackBannerClicked}
        variant="secondary"
        size="lg"
        py="2.5"
        px="6"
        position={{ sm: 'absolute' }}
        top={{ sm: '50%' }}
        right={{ sm: '2' }}
        transform={{ sm: 'translateY(-50%)' }}
      >
        View {investment ? `${investment.offering.companyName}` : 'Settings'}
      </Button>
    </Flex>
  );
};
